import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Video, 
  Headphones, 
  Activity, 
  Image, 
  FileText,
  Search,
  Filter,
  Heart,
  Clock,
  Star,
  Lock,
  ChevronRight,
  X,
  Check,
  ArrowLeft,
  Bookmark,
  Eye
} from 'lucide-react';

const CONTENT_TYPE_ICONS = {
  article: BookOpen,
  video: Video,
  audio: Headphones,
  exercise: Activity,
  infographic: Image,
  worksheet: FileText
};

const CATEGORY_LABELS = {
  anxiety: 'Anxiety',
  depression: 'Depression',
  stress: 'Stress Management',
  sleep: 'Sleep',
  mindfulness: 'Mindfulness',
  relationships: 'Relationships',
  self_care: 'Self-Care',
  coping_skills: 'Coping Skills',
  grief: 'Grief & Loss',
  trauma: 'Trauma',
  general_wellness: 'General Wellness'
};

const CATEGORY_COLORS = {
  anxiety: 'bg-purple-500/20 text-purple-400',
  depression: 'bg-blue-500/20 text-blue-400',
  stress: 'bg-orange-500/20 text-orange-400',
  sleep: 'bg-indigo-500/20 text-indigo-400',
  mindfulness: 'bg-cyan-500/20 text-cyan-400',
  relationships: 'bg-pink-500/20 text-pink-400',
  self_care: 'bg-green-500/20 text-green-400',
  coping_skills: 'bg-yellow-500/20 text-yellow-400',
  grief: 'bg-slate-500/20 text-slate-400',
  trauma: 'bg-red-500/20 text-red-400',
  general_wellness: 'bg-emerald-500/20 text-emerald-400'
};

const ContentLibrary = () => {
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [featuredContent, setFeaturedContent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewingContent, setViewingContent] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedType) params.append('content_type', selectedType);
      if (searchQuery) params.append('search', searchQuery);
      params.append('page', page);
      params.append('limit', 12);
      
      const response = await api.get(`/content/?${params.toString()}`);
      setContent(response.data.items);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedType, searchQuery, page]);

  const fetchFeatured = useCallback(async () => {
    try {
      const response = await api.get('/content/featured?limit=4');
      setFeaturedContent(response.data.items);
    } catch (error) {
      console.error('Failed to fetch featured:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/content/categories');
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  useEffect(() => {
    fetchFeatured();
    fetchCategories();
  }, [fetchFeatured, fetchCategories]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchContent();
  };

  const openContent = async (item) => {
    try {
      const response = await api.get(`/content/${item.id}`);
      if (response.data.is_locked) {
        toast.error('This is premium content. Upgrade to access.');
        return;
      }
      setViewingContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    }
  };

  const toggleLike = async (contentId, e) => {
    e.stopPropagation();
    try {
      const response = await api.post(`/content/${contentId}/like`);
      toast.success(response.data.message);
      fetchContent();
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const markComplete = async (contentId) => {
    try {
      await api.post(`/content/${contentId}/progress?progress_percent=100&completed=true`);
      toast.success('Content marked as complete!');
      setViewingContent(prev => prev ? {...prev, user_progress: { completed: true, progress_percent: 100 }} : null);
    } catch (error) {
      toast.error('Failed to mark complete');
    }
  };

  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedType(null);
    setSearchQuery('');
    setPage(1);
  };

  // Content Viewer Component
  if (viewingContent) {
    const IconComponent = CONTENT_TYPE_ICONS[viewingContent.content_type] || BookOpen;
    
    return (
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button 
            onClick={() => setViewingContent(null)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Library
          </button>

          {/* Content Header */}
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            {viewingContent.thumbnail_url && (
              <div className="h-48 md:h-64 overflow-hidden">
                <img 
                  src={viewingContent.thumbnail_url} 
                  alt={viewingContent.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            <div className="p-6 md:p-8">
              {/* Meta */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${CATEGORY_COLORS[viewingContent.category] || 'bg-muted text-muted-foreground'}`}>
                  {CATEGORY_LABELS[viewingContent.category] || viewingContent.category}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <IconComponent className="w-3 h-3" />
                  {viewingContent.content_type}
                </span>
                {viewingContent.duration_minutes && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {viewingContent.duration_minutes} min
                  </span>
                )}
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Eye className="w-3 h-3" />
                  {viewingContent.view_count} views
                </span>
              </div>

              {/* Title */}
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                {viewingContent.title}
              </h1>

              {/* Author */}
              {viewingContent.author && (
                <p className="text-sm text-muted-foreground mb-4">
                  By {viewingContent.author}
                </p>
              )}

              {/* Description */}
              <p className="text-muted-foreground mb-6">
                {viewingContent.description}
              </p>

              {/* Tags */}
              {viewingContent.tags && viewingContent.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {viewingContent.tags.map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 mb-8">
                <button
                  onClick={() => toggleLike(viewingContent.id, { stopPropagation: () => {} })}
                  className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/70 rounded-xl transition-colors"
                >
                  <Heart className="w-4 h-4" />
                  {viewingContent.like_count}
                </button>
                {!viewingContent.user_progress?.completed && (
                  <button
                    onClick={() => markComplete(viewingContent.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-xl transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    Mark Complete
                  </button>
                )}
                {viewingContent.user_progress?.completed && (
                  <span className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-xl">
                    <Check className="w-4 h-4" />
                    Completed
                  </span>
                )}
              </div>

              {/* Content Body */}
              {viewingContent.content_body && (
                <div className="prose prose-invert max-w-none">
                  <div 
                    className="text-foreground leading-relaxed whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ 
                      __html: viewingContent.content_body
                        .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-8 mb-4 text-foreground">$1</h1>')
                        .replace(/^## (.+)$/gm, '<h2 class="text-xl font-semibold mt-6 mb-3 text-foreground">$1</h2>')
                        .replace(/^### (.+)$/gm, '<h3 class="text-lg font-medium mt-4 mb-2 text-foreground">$1</h3>')
                        .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>')
                        .replace(/^- (.+)$/gm, '<li class="ml-4 text-muted-foreground">• $1</li>')
                        .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 text-muted-foreground">$1. $2</li>')
                        .replace(/---/g, '<hr class="my-6 border-border" />')
                    }}
                  />
                </div>
              )}

              {/* External URL */}
              {viewingContent.content_url && (
                <a
                  href={viewingContent.content_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-primary text-primary-foreground rounded-xl hover:opacity-90 transition-opacity"
                >
                  View Resource
                  <ChevronRight className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            Content Library
          </h1>
          <p className="text-muted-foreground">
            Curated mental health resources, exercises, and educational content
          </p>
        </div>

        {/* Search & Filters */}
        <div className="mb-8">
          <form onSubmit={handleSearch} className="flex gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles, exercises, videos..."
                className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="content-search-input"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 border rounded-xl transition-colors ${showFilters ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-foreground hover:bg-muted'}`}
            >
              <Filter className="w-5 h-5" />
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-primary text-primary-foreground rounded-xl hover:opacity-90 transition-opacity"
            >
              Search
            </button>
          </form>

          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-card border border-border rounded-xl p-4 mb-4" data-testid="filters-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-foreground">Filters</h3>
                <button onClick={clearFilters} className="text-sm text-primary hover:underline">
                  Clear all
                </button>
              </div>
              
              {/* Content Type Filter */}
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-2">Content Type</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(CONTENT_TYPE_ICONS).map(([type, Icon]) => (
                    <button
                      key={type}
                      onClick={() => setSelectedType(selectedType === type ? null : type)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedType === type 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted text-foreground hover:bg-muted/70'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <p className="text-sm text-muted-foreground mb-2">Category</p>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => setSelectedCategory(selectedCategory === cat.name ? null : cat.name)}
                      className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === cat.name 
                          ? 'bg-primary text-primary-foreground' 
                          : CATEGORY_COLORS[cat.name] || 'bg-muted text-foreground'
                      }`}
                    >
                      {cat.label} ({cat.count})
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Active Filters */}
          {(selectedCategory || selectedType || searchQuery) && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {selectedCategory && (
                <span className="flex items-center gap-1 px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
                  {CATEGORY_LABELS[selectedCategory]}
                  <button onClick={() => setSelectedCategory(null)}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedType && (
                <span className="flex items-center gap-1 px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
                  {selectedType}
                  <button onClick={() => setSelectedType(null)}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="flex items-center gap-1 px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
                  "{searchQuery}"
                  <button onClick={() => setSearchQuery('')}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Featured Section (only show if no filters) */}
        {!selectedCategory && !selectedType && !searchQuery && featuredContent.length > 0 && (
          <div className="mb-10">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              Featured
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredContent.map((item) => {
                const IconComponent = CONTENT_TYPE_ICONS[item.content_type] || BookOpen;
                return (
                  <button
                    key={item.id}
                    onClick={() => openContent(item)}
                    className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all text-left"
                    data-testid={`featured-${item.id}`}
                  >
                    {item.thumbnail_url && (
                      <div className="h-32 overflow-hidden">
                        <img 
                          src={item.thumbnail_url} 
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <IconComponent className="w-4 h-4 text-muted-foreground" />
                        <span className={`px-2 py-0.5 rounded-full text-xs ${CATEGORY_COLORS[item.category]}`}>
                          {CATEGORY_LABELS[item.category]}
                        </span>
                        {item.is_locked && <Lock className="w-3 h-3 text-yellow-400" />}
                      </div>
                      <h3 className="font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                        {item.title}
                      </h3>
                      {item.duration_minutes && (
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {item.duration_minutes} min
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-4">
            {selectedCategory ? CATEGORY_LABELS[selectedCategory] : 'All Resources'}
          </h2>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-card border border-border rounded-xl overflow-hidden animate-pulse">
                  <div className="h-40 bg-muted" />
                  <div className="p-4">
                    <div className="h-4 w-20 bg-muted rounded mb-3" />
                    <div className="h-5 w-3/4 bg-muted rounded mb-2" />
                    <div className="h-4 w-full bg-muted rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : content.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No content found</h3>
              <p className="text-muted-foreground mb-4">Try adjusting your search or filters</p>
              <button onClick={clearFilters} className="text-primary hover:underline">
                Clear filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {content.map((item) => {
                  const IconComponent = CONTENT_TYPE_ICONS[item.content_type] || BookOpen;
                  return (
                    <button
                      key={item.id}
                      onClick={() => openContent(item)}
                      className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all text-left"
                      data-testid={`content-${item.id}`}
                    >
                      {item.thumbnail_url && (
                        <div className="h-40 overflow-hidden relative">
                          <img 
                            src={item.thumbnail_url} 
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          {item.is_locked && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <Lock className="w-8 h-8 text-yellow-400" />
                            </div>
                          )}
                          {item.is_featured && !item.is_locked && (
                            <div className="absolute top-2 right-2">
                              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                            </div>
                          )}
                        </div>
                      )}
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <IconComponent className="w-4 h-4 text-muted-foreground" />
                          <span className={`px-2 py-0.5 rounded-full text-xs ${CATEGORY_COLORS[item.category]}`}>
                            {CATEGORY_LABELS[item.category]}
                          </span>
                          {item.is_premium && !item.is_locked && (
                            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
                              Pro
                            </span>
                          )}
                        </div>
                        <h3 className="font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors mb-2">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                          {item.description}
                        </p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <div className="flex items-center gap-3">
                            {item.duration_minutes && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {item.duration_minutes} min
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {item.view_count}
                            </span>
                          </div>
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {item.like_count}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-card border border-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 bg-card border border-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentLibrary;
